import { createInitialGame, isLegalMove, playMove, positionKey } from './engine';
import { moveFromUci, moveToUci } from './pikafishBridge';
import type { Difficulty, GameState, Move, Side } from './types';

interface OpeningLine {
  id: string;
  name: string;
  moves: string[];
}

interface RepertoireLine {
  id: string;
  weight: number;
}

interface Repertoire {
  maxPly: number;
  red: RepertoireLine[];
  black: RepertoireLine[];
}

interface CompiledCandidate {
  move: Move;
  weight: number;
  names: Set<string>;
}

export interface OpeningChoice {
  move: Move;
  name: string;
  candidateCount: number;
}

const lines: OpeningLine[] = [
  {
    id: 'central-screen',
    name: '中炮・屏風馬',
    moves: ['h2e2', 'h9g7', 'h0g2', 'b9c7', 'i0h0', 'i9h9', 'c3c4', 'g6g5', 'b0c2', 'c6c5', 'g3g4', 'c7d5'],
  },
  {
    id: 'central-screen-alt',
    name: '中炮・屏風馬',
    moves: ['h2e2', 'b9c7', 'h0g2', 'h9g7', 'i0h0', 'i9h9', 'c3c4', 'g6g5', 'b0c2', 'c6c5', 'g3g4', 'c7d5'],
  },
  {
    id: 'central-counter',
    name: '中炮・順炮',
    moves: ['h2e2', 'h7e7', 'h0g2', 'h9g7', 'i0h0', 'i9h9', 'c3c4', 'b9c7', 'b0c2', 'c6c5', 'g3g4', 'g6g5'],
  },
  {
    id: 'central-palace-horse',
    name: '中炮・反宮馬',
    moves: ['h2e2', 'b9c7', 'h0g2', 'h7f7', 'i0h0', 'h9g7', 'c3c4', 'i9h9', 'b0c2', 'g6g5', 'g3g4', 'c6c5'],
  },
  {
    id: 'central-elephant',
    name: '中炮・飛象守勢',
    moves: ['h2e2', 'g9e7', 'h0g2', 'h9g7', 'i0h0', 'i9h9', 'c3c4', 'c6c5', 'b0c2', 'b9c7', 'g3g4', 'g6g5'],
  },
  {
    id: 'central-rapid-rook',
    name: '中炮・過河車',
    moves: ['h2e2', 'h9g7', 'h0g2', 'b9c7', 'i0h0', 'i9h9', 'h0h6', 'g6g5', 'c3c4', 'c6c5', 'b0c2', 'h7i7'],
  },
  {
    id: 'immortal-bottom-cannon',
    name: '仙人指路・卒底炮',
    moves: ['g3g4', 'h7g7', 'b2e2', 'g9e7', 'b0c2', 'b9c7', 'h0g2', 'h9i7', 'i0h0', 'i9h9', 'c3c4', 'c6c5'],
  },
  {
    id: 'immortal-center',
    name: '仙人指路・轉中炮',
    moves: ['g3g4', 'h7e7', 'b2e2', 'b9c7', 'b0c2', 'h9g7', 'h0g2', 'i9h9', 'i0h0', 'g6g5', 'c3c4', 'c6c5'],
  },
  {
    id: 'flying-elephant',
    name: '飛相・穩健出子',
    moves: ['g0e2', 'g9e7', 'h0g2', 'h9g7', 'i0h0', 'i9h9', 'c3c4', 'c6c5', 'b0c2', 'b9c7', 'g3g4', 'g6g5'],
  },
  {
    id: 'flying-elephant-center',
    name: '飛相・對中炮',
    moves: ['g0e2', 'h7e7', 'h0g2', 'h9g7', 'i0h0', 'i9h9', 'c3c4', 'b9c7', 'b0c2', 'c6c5'],
  },
  {
    id: 'flying-elephant-guarded',
    name: '飛相・仕角固守',
    moves: ['g0e2', 'g9e7', 'd0e1', 'd9e8', 'h0g2', 'h9g7', 'i0h0', 'i9h9', 'c3c4', 'c6c5'],
  },
  {
    id: 'advisor-cannon',
    name: '仕角炮・巡河出子',
    moves: ['h2f2', 'h9g7', 'h0g2', 'b9c7', 'i0h0', 'i9h9', 'g3g4', 'g6g5', 'b0c2', 'c6c5'],
  },
  {
    id: 'advisor-cannon-guarded',
    name: '仕角炮・士象全',
    moves: ['h2f2', 'g9e7', 'g0e2', 'h9g7', 'h0g2', 'b9c7', 'd0e1', 'd9e8', 'i0h0', 'i9h9'],
  },
  {
    id: 'cross-palace-cannon',
    name: '過宮炮・緩攻',
    moves: ['h2d2', 'h9g7', 'h0g2', 'b9c7', 'c3c4', 'c6c5', 'i0h0', 'i9h9', 'b0c2', 'g6g5'],
  },
  {
    id: 'horse-opening',
    name: '起馬局・雙馬正出',
    moves: ['h0g2', 'h9g7', 'c3c4', 'c6c5', 'i0h0', 'i9h9', 'b0c2', 'b9c7', 'g3g4', 'g6g5'],
  },
  {
    id: 'two-pawns',
    name: '對兵局・互進七兵',
    moves: ['c3c4', 'c6c5', 'h0g2', 'h9g7', 'b0c2', 'b9c7', 'i0h0', 'i9h9', 'g3g4', 'g6g5'],
  },
  {
    id: 'river-cannon',
    name: '巡河炮・伺機',
    moves: ['h2h4', 'h9g7', 'h0g2', 'b9c7', 'c3c4', 'c6c5', 'i0h0', 'i9h9', 'b0c2', 'g6g5'],
  },
];

const pick = (id: string, weight: number): RepertoireLine => ({ id, weight });

export const OPENING_REPERTOIRES: Record<Difficulty, Repertoire> = {
  beginner: {
    maxPly: 8,
    red: [pick('horse-opening', 6), pick('central-screen', 4), pick('two-pawns', 2)],
    black: [pick('horse-opening', 6), pick('central-screen', 5), pick('two-pawns', 3), pick('flying-elephant', 2)],
  },
  trader: {
    maxPly: 12,
    red: [pick('central-counter', 6), pick('central-screen', 4), pick('flying-elephant-guarded', 2)],
    black: [pick('central-counter', 7), pick('central-screen', 4), pick('flying-elephant-guarded', 2)],
  },
  cannonhorse: {
    maxPly: 12,
    red: [pick('central-screen', 7), pick('central-screen-alt', 4), pick('central-palace-horse', 5), pick('horse-opening', 3)],
    black: [pick('central-screen', 7), pick('central-screen-alt', 5), pick('central-palace-horse', 6), pick('horse-opening', 3)],
  },
  gambiteer: {
    maxPly: 12,
    red: [pick('central-rapid-rook', 9), pick('central-counter', 6), pick('immortal-center', 4)],
    black: [pick('central-counter', 8), pick('central-palace-horse', 5), pick('immortal-center', 4)],
  },
  attacker: {
    maxPly: 12,
    red: [pick('central-rapid-rook', 8), pick('central-counter', 5), pick('immortal-center', 3)],
    black: [pick('central-counter', 8), pick('immortal-center', 5), pick('central-rapid-rook', 3)],
  },
  defender: {
    maxPly: 12,
    red: [pick('flying-elephant', 7), pick('central-elephant', 5), pick('cross-palace-cannon', 3)],
    black: [pick('central-screen', 7), pick('central-screen-alt', 4), pick('central-elephant', 6), pick('flying-elephant-center', 3)],
  },
  pawn: {
    maxPly: 14,
    red: [pick('immortal-bottom-cannon', 10), pick('immortal-center', 7), pick('two-pawns', 8), pick('flying-elephant', 3)],
    black: [pick('immortal-bottom-cannon', 9), pick('two-pawns', 8), pick('central-screen', 4), pick('flying-elephant', 3)],
  },
  miser: {
    maxPly: 10,
    red: [pick('flying-elephant-guarded', 8), pick('advisor-cannon-guarded', 6), pick('advisor-cannon', 3)],
    black: [pick('flying-elephant-guarded', 8), pick('advisor-cannon-guarded', 6), pick('central-elephant', 4)],
  },
  blockader: {
    maxPly: 14,
    red: [pick('cross-palace-cannon', 8), pick('river-cannon', 7), pick('central-palace-horse', 5), pick('flying-elephant', 4)],
    black: [pick('central-screen', 9), pick('central-palace-horse', 7), pick('central-elephant', 5), pick('river-cannon', 3)],
  },
  scholar: {
    maxPly: 16,
    red: [
      pick('central-screen', 5), pick('central-counter', 4), pick('central-palace-horse', 4), pick('central-rapid-rook', 3),
      pick('immortal-bottom-cannon', 5), pick('immortal-center', 4), pick('flying-elephant', 5), pick('advisor-cannon', 4),
      pick('cross-palace-cannon', 4), pick('horse-opening', 4), pick('two-pawns', 3), pick('river-cannon', 3),
    ],
    black: [
      pick('central-screen', 6), pick('central-screen-alt', 5), pick('central-counter', 4), pick('central-palace-horse', 5),
      pick('immortal-bottom-cannon', 4), pick('central-elephant', 4), pick('flying-elephant', 4), pick('advisor-cannon-guarded', 3),
      pick('cross-palace-cannon', 3), pick('horse-opening', 4), pick('two-pawns', 3), pick('river-cannon', 3),
    ],
  },
  veteran: {
    maxPly: 12,
    red: [pick('immortal-bottom-cannon', 8), pick('two-pawns', 6), pick('flying-elephant', 3)],
    black: [pick('immortal-bottom-cannon', 8), pick('two-pawns', 6), pick('central-screen', 3)],
  },
  master: {
    maxPly: 12,
    red: [
      pick('central-screen', 6), pick('central-screen-alt', 4), pick('central-palace-horse', 5), pick('central-rapid-rook', 4),
      pick('immortal-bottom-cannon', 5), pick('flying-elephant', 4), pick('advisor-cannon', 2), pick('horse-opening', 2),
    ],
    black: [
      pick('central-screen', 7), pick('central-screen-alt', 5), pick('central-palace-horse', 6), pick('central-counter', 4),
      pick('immortal-bottom-cannon', 5), pick('central-elephant', 4), pick('flying-elephant', 3), pick('horse-opening', 2),
    ],
  },
};

const lineById = new Map(lines.map((line) => [line.id, line]));
const compiled = new Map<string, CompiledCandidate[]>();

function mapKey(difficulty: Difficulty, side: Side, state: GameState): string {
  return `${difficulty}|${side}|${positionKey(state.board, state.sideToMove)}`;
}

function addCandidate(key: string, move: Move, name: string, weight: number): void {
  const candidates = compiled.get(key) ?? [];
  const uci = moveToUci(move);
  const existing = candidates.find((candidate) => moveToUci(candidate.move) === uci);
  if (existing) {
    existing.weight += weight;
    existing.names.add(name);
  } else {
    candidates.push({ move, weight, names: new Set([name]) });
  }
  compiled.set(key, candidates);
}

function compileRepertoire(difficulty: Difficulty, side: Side, repertoire: RepertoireLine[], maxPly: number): void {
  for (const entry of repertoire) {
    const line = lineById.get(entry.id);
    if (!line) throw new Error(`找不到開局譜線：${entry.id}`);
    let state = createInitialGame(1);
    for (let ply = 0; ply < line.moves.length; ply += 1) {
      const text = line.moves[ply];
      const move = text ? moveFromUci(text) : null;
      if (!move || !isLegalMove(state.board, state.sideToMove, move)) {
        throw new Error(`${line.name} 第 ${ply + 1} 手不合法：${text ?? ''}`);
      }
      if (state.sideToMove === side && ply < maxPly) addCandidate(mapKey(difficulty, side, state), move, line.name, entry.weight);
      state = playMove(state, move);
    }
  }
}

for (const difficulty of Object.keys(OPENING_REPERTOIRES) as Difficulty[]) {
  const repertoire = OPENING_REPERTOIRES[difficulty];
  compileRepertoire(difficulty, 'red', repertoire.red, repertoire.maxPly);
  compileRepertoire(difficulty, 'black', repertoire.black, repertoire.maxPly);
}

function seededRandom(seed: number): () => number {
  let value = seed >>> 0;
  return () => {
    value += 0x6d2b79f5;
    let next = value;
    next = Math.imul(next ^ (next >>> 15), next | 1);
    next ^= next + Math.imul(next ^ (next >>> 7), next | 61);
    return ((next ^ (next >>> 14)) >>> 0) / 4294967296;
  };
}

function difficultySeed(difficulty: Difficulty): number {
  let value = 2166136261;
  for (const char of difficulty) value = Math.imul(value ^ char.charCodeAt(0), 16777619);
  return value >>> 0;
}

function displayName(names: Set<string>): string {
  const values = [...names];
  if (values.length === 1) return values[0]!;
  const families = new Set(values.map((name) => name.split('・')[0] ?? name));
  return families.size === 1 ? `${[...families][0]}布局` : '熟譜布局';
}

export function lookupOpeningMove(state: GameState, difficulty: Difficulty): OpeningChoice | null {
  if (state.outcome !== 'ongoing') return null;
  const candidates = compiled.get(mapKey(difficulty, state.sideToMove, state));
  if (!candidates?.length) return null;
  const legal = candidates.filter((candidate) => isLegalMove(state.board, state.sideToMove, candidate.move));
  if (!legal.length) return null;
  const random = seededRandom(state.seed ^ difficultySeed(difficulty) ^ Math.imul(state.moves.length + 1, 0x9e3779b1));
  let pickValue = random() * legal.reduce((sum, candidate) => sum + candidate.weight, 0);
  let selected = legal[legal.length - 1]!;
  for (const candidate of legal) {
    pickValue -= candidate.weight;
    if (pickValue <= 0) {
      selected = candidate;
      break;
    }
  }
  return { move: { ...selected.move }, name: displayName(selected.names), candidateCount: legal.length };
}

export function openingBookMatch(state: GameState, difficulty: Difficulty, move: Move): string | null {
  const candidates = compiled.get(mapKey(difficulty, state.sideToMove, state));
  if (!candidates?.length) return null;
  const target = moveToUci(move);
  const matched = candidates.find((candidate) => moveToUci(candidate.move) === target);
  return matched ? displayName(matched.names) : null;
}

export function openingBookStats(): Record<Difficulty, { red: number; black: number }> {
  const result = {} as Record<Difficulty, { red: number; black: number }>;
  for (const difficulty of Object.keys(OPENING_REPERTOIRES) as Difficulty[]) {
    result[difficulty] = { red: 0, black: 0 };
  }
  for (const key of compiled.keys()) {
    const [difficulty, side] = key.split('|') as [Difficulty, Side];
    result[difficulty][side] += 1;
  }
  return result;
}
